// src/pages/CaseStudyDetails.jsx
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import { caseStudyDetailsData } from "../data/caseStudyDetailsData";
import { FaStar } from "react-icons/fa";

export default function CaseStudyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const cs = caseStudyDetailsData.find((item) => item.id === parseInt(id));
  if (!cs) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold">Case Study Not Found</h1>
      </div>
    );
  }

  const handleNextCase = () => {
    const currentIndex = caseStudyDetailsData.findIndex(
      (item) => item.id === Number(id)
    );
    if (currentIndex === -1) return;

    const nextCase = caseStudyDetailsData[currentIndex + 1];
    if (nextCase) {
      navigate(`/case-studies/${nextCase.id}`);
    } else {
      navigate(`/case-studies/${caseStudyDetailsData[0].id}`);
    }
  };

  return (
    <div className="bg-white text-gray-800">
      <Header />

      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-r from-[#264864] to-[#35a89d] text-white text-center mt-20">
        <h1 className="text-5xl font-extrabold">Project Details</h1>
      </section>

      {/* Sub Header */}
      {cs.sub_headers && (
        <section className="py-20 bg-gradient-to-r from-[#35a89d] to-[#264864] text-white">
          <div className="grid md:grid-cols-2 gap-28 items-center max-w-7xl mx-auto px-6 md:px-12">
            <div>
              {cs.sub_headers.title && (
                <h2 className="text-4xl font-bold mb-8">
                  {cs.sub_headers.title} Case Study
                </h2>
              )}
              {cs.sub_headers.sub_points?.length > 0 && (
                <ul className="list-disc pl-6 space-y-4 text-lg mb-8">
                  {cs.sub_headers.sub_points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              )}
              <Link
                to="/request-demo"
                className="inline-block bg-transparent text-white border-2 border-white px-6 py-3 rounded-none font-bold shadow hover:bg-white hover:text-black transition-colors duration-300"
              >
                Request a Quote
              </Link>
            </div>
            {cs.sub_headers.mockupImage && (
              <img
                src={cs.sub_headers.mockupImage}
                alt="about mockup"
                className="w-full h-80 rounded-2xl shadow-xl object-cover"
              />
            )}
          </div>
        </section>
      )}

            {/* About */}
      {cs.about && (
        <section className="py-20 bg-gradient-to-r from-[#264864] to-[#35a89d] text-white">
          <h2 className="text-4xl font-bold text-center mb-14">About</h2>
          <div className="grid md:grid-cols-2 gap-20 items-center max-w-7xl mx-auto px-10">
            <div className="space-y-4 text-lg">
              {cs.about.brand && <p><strong>Brand:</strong> {cs.about.brand}</p>}
              {cs.about.location && <p><strong>Location:</strong> {cs.about.location}</p>}
              {cs.about.industry && <p><strong>Industry:</strong> {cs.about.industry}</p>}
              {cs.about.type && <p><strong>Type:</strong> {cs.about.type}</p>}
              {cs.about.workScope && <p><strong>Scope of Work:</strong> {cs.about.workScope}</p>}
              {cs.about.productLink && <p><strong>Product Link:</strong> {cs.about.productLink}</p>}
            </div>
            {cs.about.sub_header_Image && (
              <img
                src={cs.about.sub_header_Image}
                alt="logo"
                className="w-full h-80 rounded-2xl mx-auto object-cover"
              />
            )}
          </div>
        </section>
      )}

      {/* Features */}
      {cs.features?.length > 0 && (
        <section className="py-20 bg-gradient-to-r from-[#e0f2ff] to-[#f0f8ff] text-gray-800">
          <h2 className="text-4xl font-bold text-center mb-14 text-gray-900">
            Key Features
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-7xl mx-auto px-10">
            {cs.features.map((feature) => {
              const formattedId =
                feature.id < 10 ? `0${feature.id}.` : `${feature.id}.`;
              return (
                <div
                  key={feature.id}
                  className="p-8 bg-white text-gray-800 shadow-lg rounded-xl"
                >
                  <div className="mb-4">
                    <span
                      style={{
                        WebkitTextStroke: "2px #3b82f6",
                        color: "transparent",
                        fontSize: "3rem",
                        fontWeight: "800",
                        letterSpacing: "0.1em",
                      }}
                    >
                      {formattedId}
                    </span>
                  </div>
                  {feature.title && (
                    <h3 className="font-bold text-2xl mb-6">
                      {feature.title}
                    </h3>
                  )}
                  {feature.sub_features?.length > 0 && (
                    <ul className="list-disc pl-6 space-y-3 text-lg">
                      {feature.sub_features.map((sf, j) => (
                        <li key={j}>{sf}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}



      {/* Challenges */}
      {cs.challenge && (
        <section className="py-20 bg-gradient-to-r from-[#264864] to-[#1e3a5f] text-white">
          <h2 className="text-4xl font-bold text-center mb-14">The Challenge</h2>
          <div className="grid md:grid-cols-2 gap-20 items-center max-w-7xl mx-auto px-10">
            {cs.challenge.mockupImage && (
              <img
                src={cs.challenge.mockupImage}
                alt="challenge"
                className="w-full h-80 rounded-2xl shadow-xl object-cover"
              />
            )}
            {cs.challenge.challenges?.length > 0 && (
              <ul className="list-disc pl-6 space-y-4 text-lg">
                {cs.challenge.challenges.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* Solutions */}
      {cs.solutions && (
        <section className="py-20 bg-gradient-to-r from-[#35a89d] to-[#2d7c74] text-white">
          <h2 className="text-4xl font-bold text-center mb-14">The Solution</h2>
          {cs.solutions.desc && (
            <p className="max-w-4xl mx-auto text-center mb-12 text-lg">
              {cs.solutions.desc}
            </p>
          )}
          <div className="grid md:grid-cols-2 gap-20 items-center max-w-7xl mx-auto px-10">
            {cs.solutions.points?.length > 0 && (
              <ul className="list-disc pl-6 space-y-4 text-lg">
                {cs.solutions.points.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            )}
            {cs.solutions.mockupImage && (
              <img
                src={cs.solutions.mockupImage}
                alt="solution"
                className="w-full h-80 rounded-2xl shadow-xl object-cover"
              />
            )}
          </div>
        </section>
      )}

      {/* Results */}
      {cs.results && (
        <section className="py-20 bg-gradient-to-r from-[#264864] to-[#1e3a5f]">
          <div className="max-w-5xl mx-auto px-10 bg-[#f0f8ff] shadow-lg py-16 rounded-2xl">
            <h2 className="text-4xl font-bold text-center mb-14 text-gray-800">
              The Results
            </h2>
            {cs.results.desc && (
              <p className="max-w-3xl mx-auto text-center mb-12 text-lg text-gray-700">
                {cs.results.desc}
              </p>
            )}
            {cs.results.metrics?.length > 0 && (
              <div className="grid md:grid-cols-2 gap-10">
                {cs.results.metrics.map((m, i) => {
                  const match = m.value.match(/(\d+\.?\d*)(.*)/);
                  const number = match ? match[1] : m.value;
                  const percent = match ? match[2] : "";

                  const isLastOdd =
                    i === cs.results.metrics.length - 1 &&
                    cs.results.metrics.length % 2 !== 0;

                  return (
                    <div
                      key={i}
                      className={`p-8 border border-gray-200 shadow text-center bg-white rounded-xl ${
                        isLastOdd ? "md:col-span-2 md:w-1/2 mx-auto" : ""
                      }`}
                    >
                      <p className="font-bold text-lg mb-2">{m.title}</p>
                      <p className="text-5xl font-extrabold bg-gradient-to-br from-blue-900 via-blue-600 to-blue-300 text-transparent bg-clip-text">
                        {number}
                        {percent && (
                          <span className="text-3xl align-top"> {percent}</span>
                        )}
                      </p>
                      {m.extra && (
                        <p className="text-lg mt-2 text-gray-800">{m.extra}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Technologies */}
      {cs.technologies?.length > 0 && (
        <section className="py-20" style={{ backgroundColor: "#f0f8ff" }}>
          <h2 className="text-4xl font-bold text-center mb-14 text-gray-900">
            Technologies
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto px-10">
            {cs.technologies.map((t, i) => (
              <div
                key={i}
                className="flex items-center gap-8 bg-white p-6 rounded-lg shadow"
              >
                <div className="text-black font-extrabold text-3xl">{t.icon}</div>
                <span className="font-bold text-gray-900">{t.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Competitive Comparison */}
      {cs.competitiveComparison && (
        <section className="py-20 bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef]">
          <h2 className="text-4xl font-bold text-center mb-14 text-gray-900">
            Competitive Comparison
          </h2>
          {cs.competitiveComparison.headers?.length > 0 && (
            <div className="max-w-6xl mx-auto overflow-x-auto px-6">
              <table className="w-full border-collapse shadow-2xl bg-white rounded-2xl">
                <thead>
                  <tr className="bg-gradient-to-r from-[#264864] to-[#35a89d] text-white text-lg rounded-t-2xl">
                    {cs.competitiveComparison.headers.map((header, i) => (
                      <th
                        key={i}
                        className="p-4 text-center font-semibold tracking-wide border border-gray-400"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cs.competitiveComparison.rows?.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`transition-transform transform hover:scale-105 hover:bg-blue-50 ${
                        rowIndex % 2 === 0 ? "bg-white" : "bg-gray-100"
                      } text-gray-800`}
                    >
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="p-4 text-center text-lg border border-gray-400"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Client Review */}
      {cs.clients?.length > 0 && (
        <section className="py-20 bg-[#f8f9fa]">
          <h2 className="text-4xl font-bold text-center mb-14">Client’s Review</h2>
          <div className="max-w-7xl mx-auto space-y-10 px-6">
            {cs.clients.map((client) => (
              <div
                key={client.id}
                className="flex flex-col md:flex-row items-start border-2 border-blue-500 rounded-lg p-6 bg-white"
              >
                {client.image && (
                  <img
                    src={client.image}
                    alt={client.name}
                    className="w-24 h-24 rounded-full object-cover mr-6 mb-4 md:mb-0"
                  />
                )}
                <div className="flex-1">
                  {client.name && (
                    <h3 className="font-bold text-xl">{client.name}</h3>
                  )}
                  {client.title && (
                    <p className="text-blue-600 font-semibold mb-4">
                      {client.title}
                    </p>
                  )}
                  {client.stars > 0 && (
                    <div className="flex items-center mb-4">
                      {[...Array(client.stars)].map((_, i) => (
                        <FaStar key={i} className="text-yellow-400 mr-1" />
                      ))}
                    </div>
                  )}
                  {client.review && (
                    <p className="text-gray-800 text-lg leading-relaxed">
                      {client.review}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#264864] to-[#35a89d] text-center text-white">
        <h2 className="text-4xl font-bold mb-10">
          Let’s start. It’s easier than you think.
        </h2>
        <div className="flex justify-center space-x-8">
          <Link
            to="/request-demo"
            className="bg-white text-[#264864] px-8 py-4 rounded-lg font-bold shadow inline-block text-center"
          >
            Contact Us
          </Link>
          <button
            onClick={handleNextCase}
            className="border border-white px-8 py-4 rounded-lg font-bold text-white hover:bg-white hover:text-[#264864] transition-colors duration-300"
          >
            Next Case →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
